"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Search, ChartBar as BarChart3, Target, Compass, MapPin, ArrowRight, CircleCheck as CheckCircle2, Zap, ShieldCheck, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keywordTrends = [{
  keyword: "SEO Consultant Bolton",
  volume: "High Commercial Intent",
  difficulty: "Medium",
  trend: "+34% YoY",
  opportunity: "Direct lead generation for professional services & local B2B brands"
}, {
  keyword: "Bolton Business Marketing",
  volume: "1,200 searches/mo",
  difficulty: "Low-Med",
  trend: "+22% YoY",
  opportunity: "Local map pack & dedicated target area optimization"
}, {
  keyword: "Ecommerce SEO North West",
  volume: "880 searches/mo",
  difficulty: "Medium",
  trend: "+45% YoY",
  opportunity: "High revenue growth for regional manufacturers & online stores"
}, {
  keyword: "Technical SEO Audit Bolton",
  volume: "High Value Enquiries",
  difficulty: "Low",
  trend: "+18% YoY",
  opportunity: "Rapid organic ranking recoveries through Core Web Vitals fixes"
}];
const competitorGaps = [{
  title: "Slow Core Web Vitals & Technical Debt",
  percentage: "68%",
  description: "68% of Bolton business websites suffer from poor LCP, cumulative layout shifts, and inefficient site structure that throttles organic reach."
}, {
  title: "Thin & Duplicate Location Copy",
  percentage: "74%",
  description: "74% of regional competitors rely on generic location templates that lack true local entity depth and structured geo-data."
}, {
  title: "Underutilized Local Schema & GBP",
  percentage: "82%",
  description: "Over 82% fail to optimize for structured LocalBusiness schema and zero-click Google Maps search features."
}];
const opportunities = [{
  title: "Google Map Pack Dominance",
  detail: "I optimize your Google Business Profile with accurate NAP alignment, geo-targeted content, and citation velocity to capture high-intent local map queries."
}, {
  title: "Entity-Based Topic Clusters",
  detail: "I structure your site around Bolton's core economic sectors—manufacturing, professional services, retail, and logistics—establishing unquestioned domain authority."
}, {
  title: "High-Authority Regional PR",
  detail: "I earn contextual backlinks from authentic North West business directories, regional news, and trusted trade platforms rather than low-quality spam."
}, {
  title: "AI Search & GEO Alignment",
  detail: "I optimize your website for Generative Engine Optimization (GEO) so your business appears in AI Overviews, ChatGPT, and perplexity answer engine results."
}];
const neighboringCities = [{
  name: "Warrington",
  distance: "21 miles",
  path: "/LocalSeoConsultantWarringtonPage"
}, {
  name: "Manchester",
  distance: "11 miles",
  path: "#"
}, {
  name: "Bury",
  distance: "6 miles",
  path: "#"
}, {
  name: "Wigan",
  distance: "10 miles",
  path: "#"
}, {
  name: "Salford",
  distance: "12 miles",
  path: "#"
}, {
  name: "Blackburn",
  distance: "12 miles",
  path: "#"
}, {
  name: "Rochdale",
  distance: "14 miles",
  path: "#"
}, {
  name: "Preston",
  distance: "20 miles",
  path: "#"
}];
export default function Bolton() {
  const [selectedKeyword, setSelectedKeyword] = useState<number>(0);
  return <section id="boltonmarketanalysis" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide">
            <MapPin className="w-3.5 h-3.5 text-primary mr-1" />
            Bolton SEO Search & Market Analysis
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-6">
            Unlocking High-Intent Search Growth in <span className="text-primary">Bolton</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
            I analyze real search trends, local competitor gaps, and organic search opportunities across Greater Manchester and Bolton so your business can capture market share directly from ready-to-buy clients.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-bold font-serif text-foreground flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                Bolton Search Demand Trends
              </h3>
              <span className="text-xs text-muted-foreground font-mono">Updated Regional Data</span>
            </div>

            <div className="grid gap-3">
              {keywordTrends.map((item, i) => {
              const isSelected = selectedKeyword === i;
              return <Card key={item.keyword} data-index={i} onClick={() => setSelectedKeyword(i)} className={`cursor-pointer transition-[border-color,box-shadow,background-color] duration-200 border ${isSelected ? "border-primary bg-card shadow-md" : "border-border bg-card/50 hover:border-border/80 hover:bg-card"}`}>
                    <CardHeader className="p-4 sm:p-5">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="font-semibold text-foreground text-base">
                          {item.keyword}
                        </span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs bg-background">
                            {item.volume}
                          </Badge>
                          <Badge className="bg-primary text-primary-foreground text-xs">
                            {item.trend}
                          </Badge>
                        </div>
                      </div>
                      <CardDescription className="text-xs sm:text-sm text-muted-foreground mt-2">
                        {item.opportunity}
                      </CardDescription>
                    </CardHeader>
                  </Card>;
            })}
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-between">
            <Card className="bg-muted/50 border-border h-full flex flex-col justify-between p-6 rounded-xl">
              <div>
                <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-4">
                  <BarChart3 className="w-4 h-4" />
                  Keyword Opportunity Deep-Dive
                </div>
                <h4 className="text-2xl font-bold font-serif text-foreground mb-3">
                  {keywordTrends[selectedKeyword].keyword}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  {keywordTrends[selectedKeyword].opportunity}
                </p>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Search Commercial Intent:</span>
                    <span className="font-semibold text-foreground">High / Transactional</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Organic Ranking Difficulty:</span>
                    <span className="font-semibold text-foreground">
                      {keywordTrends[selectedKeyword].difficulty}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Strategic Priority:</span>
                    <span className="font-semibold text-primary">Immediate Win</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <div className="p-4 rounded-lg bg-background border border-border text-xs text-muted-foreground space-y-2">
                  <div className="flex items-center gap-1.5 font-medium text-foreground">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    How I Target This Keyword
                  </div>
                  <p>
                    I build hyper-focused entity pages with verified local schema, optimized H1/H2 structures, and tailored conversion funnels to turn search traffic into booked calls.
                  </p>
                </div>
              </div>
            </Card>
          </div>

        </div>

        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="outline" className="mb-3 text-xs">
              <Compass className="w-3.5 h-3.5 text-primary mr-1" />
              Regional Market Vulnerabilities
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              Bolton Competitor Analysis & Gaps
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-2">
              Most local businesses rely on outdated SEO tactics. Here is where your competitors are failing and how we leverage it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {competitorGaps.map((gap, i) => <Card key={gap.title} data-index={i} className="bg-card border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color]">
                <CardHeader>
                  <div className="text-3xl sm:text-4xl font-extrabold text-primary font-serif mb-2">
                    {gap.percentage}
                  </div>
                  <CardTitle className="text-lg font-serif text-foreground">
                    {gap.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {gap.description}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-10 lg:p-12 mb-20 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-5 space-y-4">
              <Badge variant="secondary" className="text-xs">
                <Target className="w-3.5 h-3.5 text-primary mr-1" />
                Custom Growth Roadmap
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground leading-tight">
                My Tailored SEO Growth Strategy for Bolton Brands
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                When you work with me, you do not get outsourced junior account managers or generic AI templates. You work directly with me to execute precise, white-hat SEO strategies.
              </p>
              
              <div className="pt-2">
                <Link to="/ContactPage">
                  <Button size="lg" className="gap-2 text-sm font-medium">
                    Book a Free Bolton SEO Consultation
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {opportunities.map((opp, i) => <div key={opp.title} data-index={i} className="bg-background border border-border p-5 rounded-xl flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-foreground text-base mb-2">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span>{opp.title}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {opp.detail}
                    </p>
                  </div>
                </div>)}
            </div>

          </div>
        </div>

        <div className="border-t border-border pt-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h3 className="text-xl font-bold font-serif text-foreground flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                Serving Bolton & Neighboring North West Hubs
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Explore local SEO expertise and market analysis tailored to nearby North West towns and commercial centers.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {neighboringCities.map((city, i) => <Link key={city.name} to={city.path} data-index={i} className="group">
                <div className="bg-card hover:bg-muted/80 border border-border rounded-lg p-3.5 transition-[background-color,border-color] flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary" />
                      {city.name}
                    </div>
                    <div className="text-xs text-muted-foreground pl-5">
                      {city.distance} away
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>)}
          </div>
        </div>

      </div>
    </section>;
}